/**
 * Gestión de usuarios y asignación de roles.
 */
import React, { useState } from 'react';
import { initialUserState } from '../AdminPanel'; // Importar estado inicial
// Importar iconos SVG
import EditIcon from '../../assets/icons/edit.svg';
import EyeIcon from '../../assets/icons/eye.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import SendIcon from '../../assets/icons/send.svg';
import avatar1_local from '../../assets/avatar/avatar_f.png';
import avatar2_local from '../../assets/avatar/avatar_m.png';
import avatar3_local from '../../assets/avatar/avatar_n.png';
import avatar4_local from '../../assets/avatar/avatar_b.png';
import KeyIcon from '../../assets/icons/key.svg';
import { studio, RoleBadge, StudioIcon, SearchField, FilterDock, FilterPill, FilterMeta, Field, DockActionRow, cols, FULL_ROW } from './studioUi';
import { useAuth } from '../../AuthContext';

const UserManagement = ({ users, cargarTodo, onImpersonate, styles, API_URL, userPermissions, fetchWithAuth, areasList, roles = [], embedded = false, onManageAccess }) => {
    const { withBusy } = useAuth();
    // Define la URL base de tu API, usando la variable de entorno para producción
    const API_URL_INTERNAL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const [nuevoUser, setNuevoUser] = useState(initialUserState);
    const [editandoUserUid, setEditandoUserUid] = useState(null);
    const [verPassword, setVerPassword] = useState(false);
    const [showUserForm, setShowUserForm] = useState(false);
    const [avatarSelectionMode, setAvatarSelectionMode] = useState('select');
    
    // Arrays para la visualización local y las URLs públicas para guardar
    const localAvatars = [avatar1_local, avatar2_local, avatar3_local, avatar4_local];
    // IMPORTANTE: Asegúrate de que estas URLs coincidan con la ubicación pública de tus avatares en Firebase Storage.
    const publicAvatarUrls = [
        'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_f.png?alt=media',
        'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_m.png?alt=media',
        'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_n.png?alt=media',
        'https://firebasestorage.googleapis.com/v0/b/cm-bi-f1.firebasestorage.app/o/avatars%2Favatar_b.png?alt=media'
    ];

    const [filtroRol, setFiltroRol] = useState('');
    const [filtroEmpresa, setFiltroEmpresa] = useState('');
    const [filtroArea, setFiltroArea] = useState('');
    const [filtroNombre, setFiltroNombre] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);

    const handleUserSubmit = async (e) => {
        e.preventDefault();

        const isEditing = !!editandoUserUid;

        // La restricción de no poder crear/editar Admin o PM ahora se maneja en el backend
        if ((nuevoUser.rol === 'admin' || nuevoUser.rol === 'pm') && !userPermissions.users_assign_admin_pm_roles) {
            return alert("No tienes permiso para asignar roles 'admin' o 'pm'.");
        }

        if (!nuevoUser.rol) {
            return alert("Por favor, selecciona un rol para el usuario.");
        }

        const payload = {
            nombre: nuevoUser.nombre,
            empresa: nuevoUser.empresa,
            rol: nuevoUser.rol,
            email: nuevoUser.email,
            area: nuevoUser.area,
        };

        if (nuevoUser.photoURL) {
            payload.photoURL = nuevoUser.photoURL;
        }

        if (isEditing) {
            if (nuevoUser.password && nuevoUser.password !== nuevoUser.confirmPassword) {
                return alert("Las contraseñas no coinciden.");
            }
            if (nuevoUser.password) {
                payload.password = nuevoUser.password;
            }
        } else {
            if (!nuevoUser.password) {
                return alert("Por favor, genera una contraseña para el nuevo usuario.");
            }
            payload.password = nuevoUser.password;
            payload.passwordChangeRequired = true;
        }

        console.log("UserManagement: Enviando payload para usuario", editandoUserUid || "nuevo", ":", payload); // Log para verificar el payload
        try {
            await withBusy(async () => {
            const url = isEditing ? `${API_URL_INTERNAL}/admin/users/${editandoUserUid}` : `${API_URL_INTERNAL}/admin/users`;
            const method = isEditing ? 'PUT' : 'POST';
            const res = await fetchWithAuth(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert("Operación exitosa");
                setNuevoUser(initialUserState);
                setVerPassword(false);
                setEditandoUserUid(null);
                setShowUserForm(false);
                setAvatarSelectionMode('select');
                cargarTodo();
            } else {
                const d = await res.json().catch(() => ({ error: "Error desconocido al guardar usuario." }));
                alert(d.error);
            }
            }, isEditing ? 'Guardando usuario…' : 'Creando usuario…');
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    const cancelarEdicionUsuario = () => {
        setEditandoUserUid(null);
        setNuevoUser(initialUserState);
        setShowUserForm(false);
        setVerPassword(false);
        setAvatarSelectionMode('select');
    };

    const handleResendWelcomeEmail = async (user) => {
        if (window.confirm(`¿Estás seguro de que quieres reenviar el correo de bienvenida a ${user.nombre} (${user.correo})? Esto generará una nueva contraseña temporal.`)) {
            try {
                const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/users/${user.uid}/resend-welcome-email`, {
                    method: 'POST'
                });
                const data = await res.json();
                alert(data.message);
            } catch (error) {
                alert(`Error al reenviar el correo: ${error.message}`);
            }
        }
    };

    const generateStrongPassword = () => {
        const length = 12;
        const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowercase = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const symbols = "!@#$%^&*()_+~`|}{[]:;?><,./-=";
        const allChars = uppercase + lowercase + numbers + symbols;
        let password = "";
        password += uppercase[Math.floor(Math.random() * uppercase.length)];
        password += lowercase[Math.floor(Math.random() * lowercase.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += symbols[Math.floor(Math.random() * symbols.length)];
        for (let i = 4; i < length; i++) password += allChars[Math.floor(Math.random() * allChars.length)];
        setNuevoUser({ ...nuevoUser, password: password.split('').sort(() => 0.5 - Math.random()).join('') });
    };

    // Filtrar roles que el usuario actual puede asignar.
    // Si el usuario no tiene el permiso `users_assign_admin_pm_roles`, se excluyen los roles 'admin' y 'pm'.
    const rolesDisponiblesParaAsignar = userPermissions.users_assign_admin_pm_roles
        ? roles
        : roles.filter(rol => rol.id !== 'admin' && rol.id !== 'pm');
    const empresasUnicas = [...new Set(users.map(u => u.empresa))];

    const usuariosFiltrados = users.filter(u => {
        const matchRol = filtroRol ? u.rol === filtroRol : true;
        const matchEmpresa = filtroEmpresa ? u.empresa === filtroEmpresa : true;
        const matchArea = filtroArea ? u.area === filtroArea : true;
        const term = filtroNombre.trim().toLowerCase();
        const matchNombre = !term || (u.nombre || '').toLowerCase().includes(term) || (u.correo || u.email || '').toLowerCase().includes(term);
        return matchRol && matchEmpresa && matchArea && matchNombre;
    });

    const areasUnicas = [...new Set(users.map(u => u.area))].filter(Boolean).sort(); // Obtener áreas únicas y filtrar vacías
    const usuariosPorEmpresa = usuariosFiltrados.reduce((acc, user) => {
        const empresa = user.empresa || 'Sin Empresa';
        if (!acc[empresa]) {
            acc[empresa] = [];
        }
        acc[empresa].push(user);
        return acc;
    }, {});
    const empresasOrdenadas = Object.keys(usuariosPorEmpresa).sort();

    const roleName = (roleId) => roles.find((rol) => rol.id === roleId)?.name || roleId;

    return (
        <div>
            <div style={studio.card}>
                {!embedded && <h3 style={{ margin: '0 0 16px' }}>Gestión de Usuarios</h3>}
                {showUserForm && (
                    <div style={studio.formPanel}>
                        <h3 style={studio.formTitle}>{editandoUserUid ? 'Editar usuario' : 'Registrar usuario'}</h3>
                        <form onSubmit={handleUserSubmit} style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: '14px', width: '100%' }}>
                            <Field label="Nombre">
                                <input placeholder="Nombre completo" value={nuevoUser.nombre} onChange={e=>setNuevoUser({...nuevoUser, nombre: e.target.value})} style={studio.control} required />
                            </Field>
                            <Field label="Empresa">
                                <input placeholder="Empresa" value={nuevoUser.empresa} onChange={e=>setNuevoUser({...nuevoUser, empresa: e.target.value})} style={studio.control} required />
                            </Field>
                            <Field label="Email" span={2}>
                                <input placeholder="Email" type="email" value={nuevoUser.email} onChange={e=>setNuevoUser({...nuevoUser, email: e.target.value})} style={studio.control} required />
                            </Field>
                            <div style={{ gridColumn: FULL_ROW }}>
                                <div style={{ display: 'flex', gap: '18px', marginBottom: '10px' }}>
                                    <label style={{cursor: 'pointer', fontSize: '0.9rem', color: '#3c4043'}}>
                                        <input type="radio" name="avatarMode" value="select" checked={avatarSelectionMode === 'select'} onChange={() => setAvatarSelectionMode('select')} />
                                        {' '}Seleccionar avatar
                                    </label>
                                    <label style={{cursor: 'pointer', fontSize: '0.9rem', color: '#3c4043'}}>
                                        <input type="radio" name="avatarMode" value="url" checked={avatarSelectionMode === 'url'} onChange={() => {
                                            setAvatarSelectionMode('url');
                                            if (publicAvatarUrls.includes(nuevoUser.photoURL)) {
                                                setNuevoUser({ ...nuevoUser, photoURL: '' });
                                            }
                                        }} />
                                        {' '}URL personalizada
                                    </label>
                                </div>
                                {avatarSelectionMode === 'select' && (
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', padding: '6px 0 4px' }}>
                                        {localAvatars.map((avatarSrc, index) => {
                                            const publicUrl = publicAvatarUrls[index];
                                            const selected = nuevoUser.photoURL === publicUrl;
                                            return (
                                                <img
                                                    key={index}
                                                    src={avatarSrc}
                                                    alt={`Avatar ${index + 1}`}
                                                    style={{
                                                        width: '56px',
                                                        height: '56px',
                                                        borderRadius: '50%',
                                                        cursor: 'pointer',
                                                        border: selected ? '3px solid #5E35B1' : '3px solid #e8eaed',
                                                        objectFit: 'cover',
                                                    }}
                                                    onClick={() => setNuevoUser({ ...nuevoUser, photoURL: publicUrl })}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                                {avatarSelectionMode === 'url' && ( <input placeholder="URL de foto de perfil (opcional)" value={nuevoUser.photoURL || ''} onChange={e=>setNuevoUser({...nuevoUser, photoURL: e.target.value})} style={studio.control} /> )}
                            </div>
                            {!editandoUserUid && (
                                <>
                                    <div style={{ gridColumn: FULL_ROW, display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input placeholder="Contraseña generada" type="text" value={nuevoUser.password} style={{...studio.control, flex: 1}} readOnly />
                                        <button type="button" onClick={generateStrongPassword} style={studio.ghostBtn}>Generar</button>
                                    </div>
                                    {nuevoUser.password && (
                                        <div style={{ gridColumn: FULL_ROW }}>
                                            <p style={studio.helper}>
                                                <strong>Contraseña lista.</strong> Se enviará al usuario; deberá cambiarla en su primer inicio de sesión.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                            {editandoUserUid && (
                                <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: '14px', gridColumn: FULL_ROW }}>
                                    <div style={{position:'relative'}}>
                                        <input placeholder="Nueva contraseña (opcional)" type={verPassword ? "text":"password"} value={nuevoUser.password} onChange={e=>setNuevoUser({...nuevoUser, password: e.target.value})} style={{...studio.control, width:'100%'}} />
                                        <button type="button" onClick={()=>setVerPassword(!verPassword)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#5f6368', fontWeight: 600 }}>{verPassword ? "Ocultar":"Ver"}</button>
                                    </div>
                                    <input placeholder="Confirmar nueva contraseña" type={verPassword ? "text":"password"} value={nuevoUser.confirmPassword} onChange={e=>setNuevoUser({...nuevoUser, confirmPassword: e.target.value})} style={studio.control} />
                                </div>
                            )}
                            <Field label="Rol">
                            <select value={nuevoUser.rol} onChange={e=>setNuevoUser({...nuevoUser, rol: e.target.value})} style={studio.control}>
                                <option value="">Seleccionar rol</option>
                                {rolesDisponiblesParaAsignar.map(rol => (
                                    <option key={rol.id} value={rol.id}>{rol.name}</option>
                                ))}
                            </select>
                            </Field>
                            <Field label="Área">
                            <select value={nuevoUser.area} onChange={e => setNuevoUser({...nuevoUser, area: e.target.value})} style={studio.control}>
                                <option value="">Seleccionar área</option>
                                {areasList.map(area => (
                                    <option key={area} value={area}>{area}</option>
                                ))}
                            </select>
                            </Field>

                            <div style={{ display: 'flex', gap: '10px', gridColumn: FULL_ROW, marginTop: '4px' }}>
                                <button type="submit" style={studio.primaryBtn}>{editandoUserUid ? "Actualizar usuario":"Registrar"}</button>
                                <button type="button" onClick={cancelarEdicionUsuario} style={studio.ghostBtn}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}
                <FilterDock>
                    <DockActionRow
                        action={!showUserForm && userPermissions.users_create ? (
                            <button type="button" onClick={() => setShowUserForm(true)} style={studio.primaryBtn}>Nuevo usuario</button>
                        ) : null}
                    >
                        <SearchField value={filtroNombre} onChange={setFiltroNombre} placeholder="Buscar por nombre o email…" style={{ marginBottom: 0 }} />
                    </DockActionRow>
                    <div style={studio.filterPills}>
                        <FilterPill value={filtroRol} onChange={setFiltroRol} emptyLabel="Rol">
                            {roles.map((rol) => <option key={rol.id} value={rol.id}>{rol.name}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroEmpresa} onChange={setFiltroEmpresa} emptyLabel="Empresa">
                            {empresasUnicas.map((e) => <option key={e} value={e}>{e}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroArea} onChange={setFiltroArea} emptyLabel="Área">
                            {areasUnicas.map((area) => <option key={area} value={area}>{area}</option>)}
                        </FilterPill>
                    </div>
                    <FilterMeta
                        summary={`${usuariosFiltrados.length} ${usuariosFiltrados.length === 1 ? 'persona' : 'personas'}`}
                        chips={[
                            filtroNombre && { key: 'nombre', label: `“${filtroNombre}”`, onClear: () => setFiltroNombre('') },
                            filtroRol && { key: 'rol', label: roleName(filtroRol), onClear: () => setFiltroRol('') },
                            filtroEmpresa && { key: 'empresa', label: filtroEmpresa, onClear: () => setFiltroEmpresa('') },
                            filtroArea && { key: 'area', label: filtroArea, onClear: () => setFiltroArea('') },
                        ].filter(Boolean)}
                        onClearAll={() => { setFiltroNombre(''); setFiltroRol(''); setFiltroEmpresa(''); setFiltroArea(''); }}
                    />
                </FilterDock>

                {empresasOrdenadas.length === 0 && (
                    <div style={studio.empty}>No hay personas con esos filtros.</div>
                )}

                {empresasOrdenadas.map(empresa => (
                    <div key={empresa}>
                        <div style={studio.groupHead}>{empresa} · {usuariosPorEmpresa[empresa].length}</div>
                        <table style={studio.table}>
                            <thead>
                                <tr>
                                    <th style={{...studio.th, width: '52px'}}></th>
                                    <th style={studio.th}>Nombre</th>
                                    <th style={studio.th}>Email</th>
                                    <th style={studio.th}>Área</th>
                                    <th style={studio.th}>Rol</th>
                                    <th style={studio.th}>Último acceso</th>
                                    <th style={{...studio.th, width: '180px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuariosPorEmpresa[empresa].map(u => (
                                    <tr
                                        key={u.uid}
                                        style={hoveredRow === u.uid ? studio.trHover : undefined}
                                        onMouseEnter={() => setHoveredRow(u.uid)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                    >
                                        <td style={studio.td}>
                                            <img
                                                src={u.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.nombre)}&background=5E35B1&color=fff&size=36`}
                                                alt=""
                                                style={studio.avatar}
                                            />
                                        </td>
                                        <td style={{...studio.td, fontWeight: 600}}>{u.nombre}</td>
                                        <td style={{...studio.td, color: '#5f6368'}}>{u.correo}</td>
                                        <td style={studio.td}>{u.area || '—'}</td>
                                        <td style={studio.td}><RoleBadge roleId={u.rol} label={roleName(u.rol)} /></td>
                                        <td style={{...studio.td, color: '#5f6368', whiteSpace: 'nowrap'}}>
                                            {u.lastLogin
                                                ? new Date(u.lastLogin._seconds * 1000).toLocaleString()
                                                : 'Nunca'}
                                        </td>
                                        <td style={{...studio.td, whiteSpace: 'nowrap'}}>
                                            {onManageAccess && (
                                                <button
                                                    title="Asignar reportes"
                                                    onClick={() => onManageAccess(u)}
                                                    style={studio.iconBtn}
                                                >
                                                    <StudioIcon src={KeyIcon} alt="Accesos" />
                                                </button>
                                            )}
                                            {userPermissions.users_edit && (
                                                <button
                                                    title="Editar usuario"
                                                    onClick={() => {
                                                        const isPredefinedAvatar = publicAvatarUrls.includes(u.photoURL);

                                                        setNuevoUser({ ...u, password: '', confirmPassword: '' });
                                                        setEditandoUserUid(u.uid);
                                                        setShowUserForm(true);
                                                        setAvatarSelectionMode(isPredefinedAvatar || !u.photoURL ? 'select' : 'url');
                                                    }}
                                                    style={studio.iconBtn}
                                                    disabled={!userPermissions.users_assign_admin_pm_roles && (u.rol === 'admin' || u.rol === 'pm')}
                                                >
                                                    <StudioIcon src={EditIcon} alt="Editar" />
                                                </button>
                                            )}
                                            {userPermissions.users_impersonate && (
                                                <button title="Ver como este usuario" onClick={() => onImpersonate(u)} style={studio.iconBtn}>
                                                    <StudioIcon src={EyeIcon} alt="Ver como" />
                                                </button>
                                            )}
                                            {userPermissions.users_delete && (
                                                <button title="Borrar usuario" onClick={async ()=>{
                                                    if(window.confirm("¿Borrar?")){
                                                        await fetchWithAuth(`${API_URL_INTERNAL}/admin/users/${u.uid}`,{method:'DELETE'});
                                                        cargarTodo();
                                                    }
                                                }} style={studio.iconBtnDanger}>
                                                    <StudioIcon src={TrashIcon} alt="Eliminar" />
                                                </button>
                                            )}
                                            {!u.lastLogin && userPermissions.users_edit && (
                                                <button title="Reenviar correo de bienvenida" onClick={() => handleResendWelcomeEmail(u)} style={studio.iconBtn}>
                                                    <StudioIcon src={SendIcon} alt="Reenviar" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserManagement;